import { ClipMetadata } from '../../../api/structs/clip';
import { RenderProposal, RenderTemplate } from '../../../api/renderTemplates';
import { GenericCarouselBrandPreset } from '../../../remotion/generic-carousel/brandPresets';

export type CarouselLayoutVariant =
  | 'image-led'
  | 'poster'
  | 'no-image-typography'
  | 'chat-overlay'
  | 'split-comparison'
  | 'editorial-card';

export type CarouselTextDensity = 'dramatic' | 'balanced' | 'compact';

export interface CarouselStylePresetDraft {
  layoutVariant?: CarouselLayoutVariant;
  brandTag?: string;
  logoUrl?: string;
  highlightTextureUrl?: string;
  headlineFontId?: string;
  bodyFontId?: string;
  highlightStart?: string;
  highlightEnd?: string;
  highlightIntensity?: number;
  highlightTextureStrength?: number;
  bottomOverlayStrength?: number;
  textDensity?: CarouselTextDensity;
  showDecorativeLines?: boolean;
}

export const carouselLayoutVariants: CarouselLayoutVariant[] = [
  'image-led',
  'poster',
  'no-image-typography',
  'chat-overlay',
  'split-comparison',
  'editorial-card',
];

export const carouselTextDensities: CarouselTextDensity[] = ['dramatic', 'balanced', 'compact'];

const draftKeys = new Set<keyof CarouselStylePresetDraft>([
  'layoutVariant',
  'brandTag',
  'logoUrl',
  'highlightTextureUrl',
  'headlineFontId',
  'bodyFontId',
  'highlightStart',
  'highlightEnd',
  'highlightIntensity',
  'highlightTextureStrength',
  'bottomOverlayStrength',
  'textDensity',
  'showDecorativeLines',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const pickCarouselStylePresetDraft = (value: unknown): CarouselStylePresetDraft => {
  if (!isRecord(value)) return {};
  const draft: CarouselStylePresetDraft = {};
  Object.entries(value).forEach(([key, rawValue]) => {
    if (!draftKeys.has(key as keyof CarouselStylePresetDraft) || rawValue === undefined || rawValue === null) {
      return;
    }
    (draft as Record<string, unknown>)[key] = rawValue;
  });
  return draft;
};

export const getCarouselStylePresetDraft = (metadata?: ClipMetadata): CarouselStylePresetDraft =>
  pickCarouselStylePresetDraft(metadata?.stylePresetDraft);

export const applyRenderTemplateToMetadata = (
  metadata: ClipMetadata | undefined,
  template: Pick<RenderTemplate, 'id' | 'name' | 'config'>
): ClipMetadata => ({
  ...(metadata || {}),
  stylePresetDraft: {
    ...getCarouselStylePresetDraft(metadata),
    ...pickCarouselStylePresetDraft(template.config),
  },
  stylePresetId: template.id,
  stylePresetName: template.name,
  stylePresetDirty: false,
});


export const applyBuiltinCarouselBrandPresetToMetadata = (
  metadata: ClipMetadata | undefined,
  preset: Pick<GenericCarouselBrandPreset, 'id' | 'name' | 'values'>
): ClipMetadata => ({
  ...(metadata || {}),
  brandingTag: preset.values.brandTag || metadata?.brandingTag,
  stylePresetDraft: pickCarouselStylePresetDraft(preset.values),
  stylePresetId: preset.id,
  stylePresetName: preset.name,
  stylePresetDirty: false,
});

export const updateCarouselDraftMetadata = (
  metadata: ClipMetadata | undefined,
  draft: CarouselStylePresetDraft
): ClipMetadata => ({
  ...(metadata || {}),
  stylePresetDraft: pickCarouselStylePresetDraft(draft),
  stylePresetDirty: true,
});

export const applyRenderProposalToMetadata = (
  metadata: ClipMetadata | undefined,
  proposal: Pick<RenderProposal, 'metadata_patch_json' | 'template_config_json'>
): ClipMetadata => {
  const current = metadata || {};
  const patch = isRecord(proposal.metadata_patch_json) ? proposal.metadata_patch_json : {};
  const patchDraft = pickCarouselStylePresetDraft(patch.stylePresetDraft);
  const templateDraft = pickCarouselStylePresetDraft(proposal.template_config_json);
  const next: ClipMetadata = {
    ...current,
    ...patch,
  };

  if (Object.keys(templateDraft).length > 0 || Object.keys(patchDraft).length > 0) {
    next.stylePresetDraft = {
      ...getCarouselStylePresetDraft(current),
      ...templateDraft,
      ...patchDraft,
    };
    if (typeof patch.stylePresetDirty !== 'boolean') {
      next.stylePresetDirty = true;
    }
  }

  return next;
};
